import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaCopy, FaHeart } from "react-icons/fa";
import { Helmet } from "react-helmet-async";
import "./ContactSection.css";
import { CONFIG } from "../../constants/config";

const ContactSection = () => {
  const [copyStatus, setCopyStatus] = useState("");
  const [showDonation, setShowDonation] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const email = "extenplayer@gmail.com";
  const kakaoPayUrl = "https://qr.kakaopay.com/FUcOQEh9N";

  const checkMobile = useCallback(() => {
    const isMobileDevice = /iPhone|iPad|iPod|Android/i.test(
      navigator.userAgent
    );
    if (isMobile !== isMobileDevice) {
      setIsMobile(isMobileDevice);
    }
  }, [isMobile]);

  useEffect(() => {
    checkMobile();
    let timeoutId;
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(checkMobile, 250);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(timeoutId);
    };
  }, [checkMobile]);

  const handleEmailClick = useCallback(async () => {
    try {
      if (isMobile) {
        const textArea = document.createElement("textarea");
        textArea.value = email;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
        setCopyStatus("copied");
      } else {
        await navigator.clipboard.writeText(email);
        setCopyStatus("copied");
      }

      setTimeout(() => setCopyStatus(""), 2000);
    } catch (err) {
      console.error("Copy failed:", err);
      setCopyStatus("failed");
      setTimeout(() => setCopyStatus(""), 2000);
    }
  }, [email, isMobile]);

  const handleQRClick = useCallback(() => {
    if (isMobile) {
      window.location.href = kakaoPayUrl;
    }
  }, [isMobile, kakaoPayUrl]);

  const modalVariants = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { duration: 0.3 },
  };

  const modalContentVariants = {
    initial: { scale: 0.5, opacity: 0 },
    animate: { scale: 1, opacity: 1 },
    exit: { scale: 0.5, opacity: 0 },
    transition: { duration: 0.3 },
  };

  useEffect(() => {
    if (showDonation) {
      document.body.classList.add("modal-open");
    } else {
      document.body.classList.remove("modal-open");
    }
  }, [showDonation]);

  return (
    <>
      <Helmet>
        <title>{CONFIG.META.TITLE}</title>
        <meta
          name="description"
          content="I-Projects 서비스 관련 문의사항이 있으시다면 언제든 연락주세요. 더 나은 서비스를 위해 노력하겠습니다."
        />
        <meta property="og:title" content="I-Projects" />
        <meta
          property="og:description"
          content="I-Projects 서비스 관련 문의사항이 있으시다면 언제든 연락주세요."
        />
        <meta property="og:image" content={CONFIG.DOMAIN.IMAGE_URL} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
      </Helmet>
      <motion.section
        className="contact-section"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.5 }}
      >
        <div className="contact-content">
          <motion.h2
            className="glowing-text"
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            CONTACT US
          </motion.h2>

          <motion.div
            className="email-container"
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
          >
            <button
              className="email-button glass-effect"
              onClick={handleEmailClick}
            >
              <span>{email}</span>
              <FaCopy />
            </button>
            <AnimatePresence>
              {copyStatus === "copied" && (
                <motion.div
                  className="copy-status success glass-effect"
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{
                    type: "spring",
                    stiffness: 500,
                    damping: 30,
                  }}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                  복사되었습니다
                </motion.div>
              )}
              {copyStatus === "failed" && (
                <motion.div
                  className="copy-status error glass-effect"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  복사 실패
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          <motion.div
            className="donation-container"
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.6 }}
          >
            <button
              className="donation-button glass-effect"
              onClick={() => setShowDonation(true)}
            >
              <FaHeart />
              <span>후원하기</span>
            </button>
          </motion.div>
        </div>

        <AnimatePresence mode="wait">
          {showDonation && (
            <motion.div
              className="donation-overlay"
              {...modalVariants}
              onClick={() => setShowDonation(false)}
            >
              <motion.div
                className="donation-modal glass-effect"
                {...modalContentVariants}
                onClick={(e) => e.stopPropagation()}
              >
                <h3>후원하기</h3>
                <div
                  className={`qr-container ${isMobile ? "clickable" : ""}`}
                  onClick={handleQRClick}
                >
                  <img src="/imgs/donation.png" alt="후원 QR 코드" />
                </div>
                <p>
                  QR 코드를 {isMobile ? "스캔 또는 클릭" : "스캔"}하여 후원해
                  주세요
                </p>
                <button
                  className="close-button"
                  onClick={() => setShowDonation(false)}
                >
                  닫기
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.section>
    </>
  );
};

export default React.memo(ContactSection);
